/**
 * RideroOrdersWidget - Extracted from HubDashboard.jsx
 * Shows live Ridero order summary with today's stats
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { Briefcase } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RideroOrdersWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/ridero/orders/summary`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        console.error('Failed to fetch Ridero orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full" data-testid="ridero-orders-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#e94560]/20 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-[#e94560]" />
          </div>
          <h3 className="font-bold">Ridero Orders</h3>
        </div>
        <span className="text-xs bg-[#e94560]/20 text-[#e94560] px-2.5 py-1 rounded-full font-medium animate-pulse">
          LIVE
        </span>
      </div>
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-16 bg-secondary/40 rounded-xl"></div>
          <div className="h-12 bg-secondary/40 rounded-xl"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Today's Stats */}
          <div className="p-3 rounded-xl bg-gradient-to-br from-[#e94560]/20 to-[#e94560]/5 border border-[#e94560]/30">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium">Today</span>
              <span className="text-[#e94560] font-bold text-xl">{data?.today?.total_orders || 0}</span>
            </div>
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                ✓ {data?.today?.completed || 0}
              </span>
              <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                ◎ {data?.today?.in_progress || 0}
              </span>
              <span className="px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                ○ {data?.today?.pending || 0}
              </span>
            </div>
          </div>
          {/* Total Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center p-2 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-lg font-bold text-green-400">{data?.summary?.completed || 0}</p>
              <p className="text-xs text-green-400/70">Done</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-lg font-bold text-blue-400">{data?.summary?.accepted || 0}</p>
              <p className="text-xs text-blue-400/70">Active</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-lg font-bold text-yellow-400">{data?.summary?.pending || 0}</p>
              <p className="text-xs text-yellow-400/70">Pending</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
